import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Rocket, Mail, Star, CheckCircle, Users, Zap, Target, Award, Shield, Gift } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const BetaProgram = () => {
  const [isSignedUp, setIsSignedUp] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    experience: '',
    interests: []
  });

  const betaFeatures = [
    {
      id: 1,
      name: 'AI Test Generation',
      description: 'Early access to AI-powered test case generation from user stories and requirements.',
      status: 'active',
      participants: 45,
      feedback: 4.8,
      icon: <Zap className="w-5 h-5" />
    },
    {
      id: 2,
      name: 'Mobile Testing Suite',
      description: 'Test the new mobile automation missions for iOS and Android applications.',
      status: 'recruiting',
      participants: 23,
      feedback: null,
      icon: <Target className="w-5 h-5" />
    },
    {
      id: 3,
      name: 'Collaborative Workspaces',
      description: 'Team features for sharing missions, code reviews, and progress tracking.',
      status: 'coming-soon',
      participants: 0,
      feedback: null,
      icon: <Users className="w-5 h-5" />
    }
  ];

  const benefits = [
    {
      title: 'Early Access',
      description: 'Get first access to new features before public release',
      icon: <Rocket className="w-6 h-6" />
    },
    {
      title: 'Direct Feedback Channel',
      description: 'Direct line to development team for suggestions and bug reports',
      icon: <Mail className="w-6 h-6" />
    },
    {
      title: 'Beta Tester Badge',
      description: 'Special recognition in your profile and community forums',
      icon: <Award className="w-6 h-6" />
    },
    {
      title: 'Exclusive Content',
      description: 'Access to beta-only missions and challenges',
      icon: <Gift className="w-6 h-6" />
    }
  ];

  const testimonials = [
    {
      name: 'Alex Chen',
      role: 'Senior QA Engineer',
      content: 'Being part of the beta program has been amazing. I get to influence the direction of new features and help shape the platform.',
      rating: 5
    },
    {
      name: 'Maria Rodriguez',
      role: 'Test Automation Specialist', 
      content: 'The AI test generation beta has completely changed how I approach automation. It\'s like having a smart assistant.',
      rating: 5
    },
    {
      name: 'David Kim',
      role: 'QA Team Lead',
      content: 'Great experience working with the team. They really listen to feedback and implement suggestions quickly.',
      rating: 4
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInterestToggle = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev?.interests?.includes(interest)
        ? prev?.interests?.filter(i => i !== interest)
        : [...prev?.interests, interest]
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    // Simulate form submission
    setTimeout(() => {
      setIsSignedUp(true);
    }, 1000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/10 text-green-500';
      case 'recruiting': return 'bg-blue-500/10 text-blue-500';
      case 'coming-soon': return 'bg-purple-500/10 text-purple-500';
      default: return 'bg-muted/10 text-muted-foreground';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Active Testing';
      case 'recruiting': return 'Recruiting';
      case 'coming-soon': return 'Coming Soon';
      default: return 'Unknown';
    }
  };

  return (
    <div className="space-y-12">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
          <Rocket className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-3xl font-bold">Beta Testing Program</h2>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
          Join our exclusive beta testing community and help shape the future of Selenium Sandbox. 
          Get early access to cutting-edge features and work directly with our development team.
        </p>
      </div>
      {/* Program Benefits */}
      <section className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {benefits?.map((benefit, index) => (
          <motion.div
            key={index}
            className="bg-card border border-border rounded-xl p-6 text-center hover:shadow-elevation-2 transition-all duration-300"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <div className="w-12 h-12 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
              {React.cloneElement(benefit?.icon, { className: "w-6 h-6 text-white" })}
            </div>
            <h3 className="font-semibold mb-2">{benefit?.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{benefit?.description}</p>
          </motion.div>
        ))}
      </section>
      {/* Current Beta Features */}
      <section className="space-y-6">
        <h3 className="text-2xl font-bold text-center">Current Beta Programs</h3>
        
        <div className="grid gap-6">
          {betaFeatures?.map((feature, index) => (
            <motion.div
              key={feature?.id}
              className="bg-card border border-border rounded-xl p-6 hover:shadow-elevation-2 transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                <div className="flex-1 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white">
                        {feature?.icon}
                      </div>
                      <div>
                        <h4 className="text-xl font-semibold">{feature?.name}</h4>
                        <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(feature?.status)}`}>
                          {getStatusText(feature?.status)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-muted-foreground leading-relaxed">
                    {feature?.description}
                  </p>
                </div>

                <div className="lg:w-64 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-muted/5 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-blue-500">{feature?.participants}</div>
                      <div className="text-xs text-muted-foreground">Testers</div>
                    </div>
                    
                    {feature?.feedback && (
                      <div className="bg-muted/5 rounded-lg p-3 text-center">
                        <div className="flex items-center justify-center space-x-1">
                          <span className="text-xl font-bold text-yellow-500">{feature?.feedback}</span>
                          <Star className="w-4 h-4 text-yellow-500 fill-current" />
                        </div>
                        <div className="text-xs text-muted-foreground">Rating</div>
                      </div>
                    )}
                  </div>

                  {feature?.status === 'recruiting' && (
                    <Button className="w-full">
                      Join Beta Test
                    </Button>
                  )}
                  
                  {feature?.status === 'active' && (
                    <Button variant="outline" className="w-full">
                      View Progress
                    </Button>
                  )}
                  
                  {feature?.status === 'coming-soon' && (
                    <Button variant="outline" disabled className="w-full">
                      Coming Soon
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
      {/* Sign-up Form */}
      {!isSignedUp && (
        <section className="bg-muted/5 rounded-xl p-8">
          <div className="max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-center mb-6">Join the Beta Program</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Full Name</label>
                  <Input
                    name="name"
                    type="text"
                    placeholder="Enter your full name"
                    value={formData?.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Email Address</label>
                  <Input
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    value={formData?.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Testing Experience</label>
                <select 
                  name="experience"
                  value={formData?.experience}
                  onChange={handleInputChange}
                  className="w-full bg-background border border-border rounded-md px-3 py-2"
                  required
                >
                  <option value="">Select your experience level</option>
                  <option value="beginner">Beginner (0-1 years)</option>
                  <option value="intermediate">Intermediate (2-4 years)</option>
                  <option value="advanced">Advanced (5+ years)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-3">Areas of Interest (select all that apply)</label>
                <div className="grid md:grid-cols-2 gap-3">
                  {['Web Testing', 'Mobile Testing', 'API Testing', 'Performance Testing', 'AI Features', 'UI/UX']?.map((interest) => (
                    <label key={interest} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData?.interests?.includes(interest)}
                        onChange={() => handleInterestToggle(interest)}
                        className="rounded border-border"
                      />
                      <span className="text-sm">{interest}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700 dark:text-blue-300">
                    <p className="font-medium mb-1">Beta Testing Agreement</p>
                    <p>By joining, you agree to provide constructive feedback, report bugs, and keep beta features confidential until public release.</p>
                  </div>
                </div>
              </div>

              <Button type="submit" size="lg" className="w-full">
                <Rocket className="w-5 h-5 mr-2" />
                Join Beta Program
              </Button>
            </form>
          </div>
        </section>
      )}
      {/* Success Message */}
      {isSignedUp && (
        <motion.section 
          className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-xl p-8 text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="w-16 h-16 mx-auto bg-green-500 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-green-700 dark:text-green-300 mb-2">Welcome to Beta Program!</h3>
          <p className="text-green-600 dark:text-green-400 mb-4">
            Thank you for joining! We'll send you an invitation email within 24 hours with access details.
          </p>
          <Button variant="outline" className="border-green-300 text-green-700 dark:text-green-300">
            <Mail className="w-4 h-4 mr-2" />
            Check Your Email
          </Button>
        </motion.section>
      )}
      {/* Beta Tester Testimonials */}
      <section className="space-y-6">
        <h3 className="text-2xl font-bold text-center">What Beta Testers Say</h3>
        
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials?.map((testimonial, index) => (
            <motion.div
              key={index}
              className="bg-card border border-border rounded-xl p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="flex items-center space-x-1 mb-3">
                {[...Array(5)]?.map((_, i) => (
                  <Star 
                    key={i} 
                    className={`w-4 h-4 ${i < testimonial?.rating ? 'text-yellow-500 fill-current' : 'text-muted-foreground'}`} 
                  />
                ))}
              </div>
              
              <blockquote className="text-muted-foreground mb-4 leading-relaxed">
                "{testimonial?.content}"
              </blockquote>
              
              <div>
                <div className="font-semibold">{testimonial?.name}</div>
                <div className="text-sm text-muted-foreground">{testimonial?.role}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
      {/* Program Stats */}
      <section className="bg-muted/5 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4 text-center">Beta Program Impact</h3>
        <div className="grid md:grid-cols-4 gap-6 text-center">
          <div>
            <div className="text-2xl font-bold text-blue-500 mb-1">68</div>
            <div className="text-sm text-muted-foreground">Active Beta Testers</div>
          </div>
          
          <div>
            <div className="text-2xl font-bold text-green-500 mb-1">145</div>
            <div className="text-sm text-muted-foreground">Features Tested</div>
          </div>
          
          <div>
            <div className="text-2xl font-bold text-purple-500 mb-1">324</div>
            <div className="text-sm text-muted-foreground">Bug Reports</div>
          </div>
          
          <div>
            <div className="text-2xl font-bold text-orange-500 mb-1">4.9</div>
            <div className="text-sm text-muted-foreground">Avg Satisfaction</div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BetaProgram;